import React, { useState } from 'react';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';

import { BulkInvoiceTotalDto } from '@project/types';

import { Button } from '../../../components/atoms';
import adminPaymentPartnersService from '../../../services/api/admin-payment-partners/admin-payment-partners.service';

export const AdminPaymentPartnersPaymentsScreen: React.FC = () => {
  const [value, setValue] = useState<string>('');
  const [selectedReferences, setSelectedReferences] = useState<string[]>([]);
  const [settledReferences, setSettledReferences] = useState<string[]>([]);
  const [data, setData] = useState<BulkInvoiceTotalDto[]>([]);

  const settleFinesMutation = useMutation(
    (referenceNumbers: string[]) => adminPaymentPartnersService.settleFinesBulk(referenceNumbers),
    {
      onSuccess: () => {
        toast.success('Successfully settled invoice(s)');
        setSettledReferences((prev) => [...prev, ...selectedReferences]);
        setSelectedReferences([]);
      },
      onError: () => {
        toast.error('Failed to settle invoice(s)');
      },
    },
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setData([]);
    setSettledReferences([]);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const onClickButtonGetTotal = async () => {
    const referenceArray = value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v);

    await adminPaymentPartnersService
      .getPaymentPartnerReferenceTotals(referenceArray)
      .then((result) => {
        setData(result);
        setSettledReferences([]);
      })
      .catch(() => toast.error('Payment partner reference not found'));
  };

  const onClickSettleFines = () => {
    if (selectedReferences.length === 0) {
      toast.error('Please select at least one reference to settle');
      return;
    }

    settleFinesMutation.mutate(selectedReferences);
  };

  const toggleReferenceSelection = (referenceNumber: string) => {
    if (settledReferences.includes(referenceNumber)) {
      return;
    }

    setSelectedReferences((prev) =>
      prev.includes(referenceNumber)
        ? prev.filter((ref) => ref !== referenceNumber)
        : [...prev, referenceNumber],
    );
  };

  const selectAllReferences = () => {
    const unsettledReferences = data
      .filter((item) => !settledReferences.includes(item.referenceNumber))
      .map((item) => item.referenceNumber);
    setSelectedReferences(unsettledReferences);
  };

  const clearAllSelections = () => {
    setSelectedReferences([]);
  };

  const selectedTotal = data
    .filter((d) => selectedReferences.includes(d.referenceNumber))
    .reduce((sum, d) => sum + d.total, 0);

  const grandTotal = data.reduce((sum, d) => sum + d.total, 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Partners Management</h2>

        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-2" htmlFor="payment-partner-reference">
            <span className="text-sm font-medium text-gray-700">
              Payment Reference Numbers (comma-separated):
            </span>
            <div className="flex gap-2">
              <input
                id="payment-partner-reference"
                className="border-2 border-gray-300 rounded-md px-3 py-2 flex-1 focus:border-blue-500 focus:outline-none"
                placeholder="Enter reference numbers separated by commas..."
                value={value}
                onChange={onChange}
                onBlur={onBlur}
              />
              <Button
                onClick={onClickButtonGetTotal}
                type="text"
                className="text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md font-medium"
              >
                Get Totals
              </Button>
            </div>
          </label>
        </div>
      </div>

      {data.length > 0 && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold pr-4 text-gray-800">
                Payment References ({data.length} items)
              </h3>
              <div className="flex gap-2">
                <Button
                  onClick={selectAllReferences}
                  className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700"
                >
                  Select All
                </Button>
                <Button
                  onClick={clearAllSelections}
                  className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700"
                >
                  Clear All
                </Button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Select
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference Number
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((item, index) => {
                  const isSettled = settledReferences.includes(item.referenceNumber);
                  const isSelected = selectedReferences.includes(item.referenceNumber);

                  return (
                    <tr
                      key={item.referenceNumber}
                      className={`${
                        isSettled
                          ? 'bg-gray-100 opacity-60'
                          : isSelected
                          ? 'bg-blue-50'
                          : index % 2 === 0
                          ? 'bg-white'
                          : 'bg-gray-50'
                      } ${!isSettled ? 'hover:bg-gray-100' : ''} transition-colors`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                            isSettled ? 'cursor-not-allowed' : 'cursor-pointer'
                          }`}
                          checked={isSelected}
                          onChange={() => toggleReferenceSelection(item.referenceNumber)}
                          disabled={isSettled}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm font-medium ${
                            isSettled ? 'text-gray-500 line-through' : 'text-gray-900'
                          }`}
                        >
                          {item.referenceNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div
                          className={`text-sm font-bold ${
                            isSettled ? 'text-gray-500 line-through' : 'text-gray-900'
                          }`}
                        >
                          {formatCurrency(item.total)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {isSettled && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Settled
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Grand Total: </span>
                <span className="font-bold text-lg text-gray-900">
                  {formatCurrency(grandTotal)}
                </span>
              </div>

              {selectedReferences.length > 0 && (
                <div className="text-sm text-blue-600">
                  <span className="font-medium">Selected ({selectedReferences.length}): </span>
                  <span className="font-bold text-lg">{formatCurrency(selectedTotal)}</span>
                </div>
              )}
            </div>
          </div>

          {selectedReferences.length > 0 && (
            <div className="px-6 py-4 bg-yellow-50 border-t border-yellow-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm text-yellow-800">
                    Ready to settle {selectedReferences.length} reference
                    {selectedReferences.length > 1 ? 's' : ''}
                    totaling {formatCurrency(selectedTotal)}
                  </span>
                </div>
                <Button
                  onClick={onClickSettleFines}
                  type="text"
                  className="text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={settleFinesMutation.isLoading}
                >
                  {settleFinesMutation.isLoading ? 'Settling...' : 'Settle Fines'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
